<!-- src/components/CustomerServiceContent.vue -->
<template>
  <div class="dialog-content">
    <!-- 消息历史 -->
    <div class="message-container" ref="messageContainer">
      <div
        v-for="(message, index) in messages"
        :key="index"
        :class="['message-item', message.sender]"
      >
        <div class="message-avatar">
          <el-avatar
            :src="message.sender === 'user' ? userAvatar : serviceAvatar"
            :size="36"
          ></el-avatar>
        </div>
        <div class="message-content">
          <div class="message-text">{{ message.text }}</div>
          <div class="message-time">{{ message.time }}</div>
        </div>
      </div>
    </div>
    <div class="choseServe">
      <template>
        <el-radio-group v-model="artificial">
          <el-radio :label="false">智能应答</el-radio>
          <el-radio :label="true">人工服务</el-radio>
        </el-radio-group>
      </template>
    </div>
    <!-- 输入区域 -->
    <div class="input-area">
      <el-input
        type="textarea"
        :rows="3"
        placeholder="请输入您的问题..."
        v-model="inputMessage"
        @keydown.enter.native="handleTextareaEnter"
        resize="none"
      ></el-input>
      <div class="action-buttons">
        <el-button
          type="primary"
          size="medium"
          @click="sendMessage"
          :disabled="!inputMessage.trim() || isProcessingAudio"
        >
          发送
        </el-button>
        <el-button
          size="medium"
          :disabled="!clearMessage || isProcessingAudio"
          @click="clearMessages"
        >
          清空记录
        </el-button>
        <el-button
          size="medium"
          :type="getRecordButtonType"
          @click="toggleRecording"
          :disabled="isProcessingAudio"
          :class="{ recording: isRecording }"
          :loading="isProcessingAudio && !isRecording"
        >
          <i :class="getRecordButtonIcon"></i>
          {{ getRecordButtonText }}
        </el-button>
      </div>

      <!-- 录音状态提示 -->
      <div v-if="isRecording" class="recording-status">
        <div class="recording-indicator">
          <span class="recording-dot"></span>
          <span>正在录音... {{ recordingTime }}"</span>
        </div>
        <div class="recording-tip">点击“停止录音”按钮结束并开始识别</div>
      </div>

      <!-- 处理中状态提示 (当不在录音但正在处理时显示) -->
      <div v-if="isProcessingAudio && !isRecording" class="processing-status">
        <div class="processing-indicator">
          <i class="el-icon-loading"></i>
          <span>正在处理并识别语音，请稍候...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sendMessage, closeMessage } from '@/API/servers/servers';
import RecordRTC from 'recordrtc';
import { uploadVoiceMessage } from '@/API/servers/voiceServer';
import DC from '@/util/SocketHandler';

export default {
  name: 'CustomerServiceContent',
  data() {
    return {
      inputMessage: '',
      userAvatar: require('@/assets/recycling/others/user-avatar.png'),
      serviceAvatar: require('@/assets/recycling/others/service-avatar.png'),
      messages: [],
      sessionId: '',
      artificial: false,
      // --- 录音相关状态 ---
      recordRTC: null,
      audioStream: null,
      isRecording: false,
      recordingTime: 0,
      recordingTimer: null,
      // --- 新增：音频处理状态 ---
      isProcessingAudio: false,
      // --- 自校验状态（调试用）---
      debugAudioParams: null, // 保存从设备获取的实际参数
    };
  },
  computed: {
    clearMessage() {
      return this.messages.length >= 2;
    },
    // 计算录音按钮的动态类型
    getRecordButtonType() {
      if (this.isRecording) {
        return 'danger';
      }
      return this.isProcessingAudio ? 'info' : 'default';
    },
    // 计算录音按钮的动态图标
    getRecordButtonIcon() {
      if (this.isProcessingAudio && !this.isRecording) {
        return 'el-icon-loading';
      }
      return 'el-icon-microphone';
    },
    // 计算录音按钮的动态文本
    getRecordButtonText() {
      if (this.isProcessingAudio && !this.isRecording) {
        return '处理中';
      }
      return this.isRecording ? '停止录音' : '语音输入';
    },
  },
  created() {
    this.getWsMessage();
    this.sendMessage();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  methods: {
    // 切换录音状态
    async toggleRecording() {
      if (this.isProcessingAudio) {
        // 正在处理时，不允许开始新的录音
        return;
      }
      if (this.isRecording) {
        await this.stopRecording();
      } else {
        await this.startRecording();
      }
    },

    // 开始录音 (已优化)
    async startRecording() {
      if (this.isRecording) return;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.$message.error('当前浏览器不支持录音功能（请确认用户是否授权，或者环境为HTTPS 或 localhost 下使用）');
        return;
      }

      // 自校验点1: 重置状态
      this.debugAudioParams = null;
      console.groupCollapsed('[语音录制] 开始录音流程');
      console.log('1. 正在请求麦克风权限并获取音频流...');

      try {
        // 1. 获取高质量的音频流 (关键修正)
        const audioConstraints = {
          audio: {
            channelCount: 1, // 强制单声道，ASR兼容性更好
            sampleRate: 16000, // 请求16kHz采样率
            echoCancellation: false, // 关闭回音消除，避免语音失真
            noiseSuppression: false, // 关闭噪声抑制，保留原始声音
            autoGainControl: false, // 关闭自动增益
            // 注意: 部分旧浏览器可能不支持以上高级约束，但会fallback到默认值
          },
        };

        const stream =
          await navigator.mediaDevices.getUserMedia(audioConstraints);
        this.audioStream = stream;

        // 自校验点2: 验证实际获取的音频参数
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          this.debugAudioParams = audioTrack.getSettings();
          console.log('2. ✅ 成功获取音频流，实际参数如下:');
          console.table({
            采样率: `${this.debugAudioParams.sampleRate || '未知'} Hz`,
            声道数: this.debugAudioParams.channelCount || '未知',
            设备ID:
              this.debugAudioParams.deviceId?.substring(0, 20) + '...' ||
              '未知',
            是否回音消除: this.debugAudioParams.echoCancellation || '未知',
            是否噪声抑制: this.debugAudioParams.noiseSuppression || '未知',
          });
        } else {
          console.warn('2. ⚠️ 获取音频流成功，但未找到音频轨道');
        }

        // 2. 配置RecordRTC
        // 使用从设备获取的实际采样率，或fallback到16000
        const sampleRate = this.debugAudioParams?.sampleRate || 16000;
        console.log(`3. 使用采样率 ${sampleRate}Hz 初始化 RecordRTC`);

        this.recordRTC = RecordRTC(stream, {
          type: 'audio',
          mimeType: 'audio/wav',
          sampleRate: sampleRate, // 输入采样率
          desiredSampRate: 16000, // 输出采样率 (ASR常用)
          numberOfAudioChannels: 1, // 单声道输出
          recorderType: RecordRTC.StereoAudioRecorder, // 推荐录制器
          // 简化wav配置，避免冲突
          // wav: {
          //   sampleRate: sampleRate
          // }
        });

        // 3. 开始录音并启动计时器
        this.recordRTC.startRecording();
        this.isRecording = true;
        this.recordingTime = 0;
        this.recordingTimer = setInterval(() => {
          this.recordingTime++;
        }, 1000);

        console.log(`4. 🎤 录音已开始，计时器启动`);
        this.$message.success('录音已开始，请讲话...');
      } catch (error) {
        console.groupEnd();
        let errorMsg = `无法访问麦克风: `;
        if (
          error.name === 'NotAllowedError' ||
          error.name === 'PermissionDeniedError'
        ) {
          errorMsg += '用户拒绝了麦克风权限请求。请检查浏览器设置。';
        } else if (
          error.name === 'NotFoundError' ||
          error.name === 'DevicesNotFoundError'
        ) {
          errorMsg += '未找到音频输入设备。请检查麦克风连接。';
        } else if (
          error.name === 'NotReadableError' ||
          error.name === 'TrackStartError'
        ) {
          errorMsg += '麦克风被占用或无法访问。请关闭其他使用麦克风的页面。';
        } else {
          errorMsg += error.message;
        }
        console.error('录音启动失败:', error);
        this.$message.error(errorMsg);
      }
    },

    // 停止录音 (已优化：移除了全屏loading，改为局部状态和轻提示)
    async stopRecording() {
      if (!this.isRecording || !this.recordRTC) {
        return;
      }

      console.log(`5. 停止录音指令触发，界面计时: ${this.recordingTime} 秒`);

      // 1. 更新UI状态：停止计时器，标记为处理中
      this.isRecording = false;
      this.isProcessingAudio = true; // 进入处理状态
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;

      // 给予用户即时反馈
      this.$message.info('正在处理音频...');

      try {
        // 2. 停止RecordRTC实例
        await new Promise((resolve) => {
          this.recordRTC.stopRecording(() => {
            resolve();
          });
        });

        // 3. 获取音频Blob
        let audioBlob = this.recordRTC.getBlob();
        if (!(audioBlob instanceof Blob)) {
          console.warn('getBlob()返回非标准Blob，正在转换...');
          audioBlob = new Blob([audioBlob], { type: 'audio/wav' });
        }

        // 自校验点3: 验证生成的音频文件
        console.log('6. ✅ 录音结束，生成音频文件:');
        console.log(`   - 界面计时: ${this.recordingTime}秒`);
        console.log(`   - 文件大小: ${(audioBlob.size / 1024).toFixed(2)} KB`);
        console.log(`   - 文件类型: ${audioBlob.type}`);
        console.log(`   - 是否为Blob: ${audioBlob instanceof Blob}`);

        // 4. 释放音频流资源
        if (this.audioStream) {
          this.audioStream.getTracks().forEach((track) => {
            track.stop(); // 停止每个轨道
            console.log(
              `   - 已释放音频轨道: ${track.kind} (${track.label || '未命名'})`,
            );
          });
          this.audioStream = null;
        }

        // 5. 空文件检查
        if (!audioBlob || audioBlob.size === 0) {
          console.error('❌ 录音失败: 生成的音频文件为空');
          this.$message.error('录音失败，未捕获到有效声音，请重试');
          this.isProcessingAudio = false; // 重置处理状态
          return;
        }

        // 6. 处理录音文件（上传和识别）
        await this.handleAudioRecorded(audioBlob, this.recordingTime);
      } catch (error) {
        console.error('停止录音过程异常:', error);
        this.$message.error('录音处理异常，请重试');
        this.isProcessingAudio = false; // 发生错误，重置处理状态
      } finally {
        // 7. 清理RecordRTC实例
        if (this.recordRTC) {
          this.recordRTC.destroy();
          this.recordRTC = null;
        }
      }
    },

    // 处理录音结果 (已优化：移除独立的全屏loading，状态已由stopRecording方法控制)
    async handleAudioRecorded(audioBlob, recordedDuration) {
      // 基础校验
      if (!(audioBlob instanceof Blob) || audioBlob.size === 0) {
        this.$message.error('录音文件无效，无法上传');
        this.isProcessingAudio = false;
        return;
      }
      if (recordedDuration < 1) {
        this.$message.warning('录音时间太短，请重新录制');
        this.isProcessingAudio = false;
        return;
      }
      if (recordedDuration > 60) {
        this.$message.warning('录音时间超过60秒，请重新录制');
        this.isProcessingAudio = false;
        return;
      }

      // 可选: 下载用于本地检查
      // this.downloadAudioFile(audioBlob);

      // 上传到服务器
      try {
        // 可以在此处使用一个更轻量、非阻塞的加载提示，例如在按钮区域显示（已通过 isProcessingAudio 实现）
        // 如果仍希望有全局提示，可取消下行注释，但建议保持非阻塞
        // const loading = this.$loading({ text: '正在上传并识别语音...', lock: false, target: '.input-area' });

        const formData = new FormData();
        const fileName = `audio_${Date.now()}.wav`;

        // 自校验点4: 上传前的最终检查
        console.log('7. 准备上传音频文件:');
        console.log(`   - 文件名: ${fileName}`);
        console.log(`   - 文件大小: ${audioBlob.size} 字节`);
        console.log(
          `   - 预估时长(估算): ${(audioBlob.size / 32000).toFixed(1)}秒`,
        ); // 16kHz, 16-bit, 单声道估算

        formData.append('audioFile', audioBlob, fileName);

        const res = await uploadVoiceMessage(formData);
        // 如果启用了上方的 loading，请在此处关闭: loading.close();

        if (res.code === 200) {
          if (res.data.length) {
            this.messages.push({
              text: res.data || '语音消息',
              time: this.formatTime(new Date()),
              sender: 'user',
            });
            sendMessage({
              content: res.data,
              sessionId: this.sessionId,
              artificial: this.artificial,
            })
              .then((res) => {
                if (res.code === 200) {
                  console.log('语音消息发送成功');
                } else {
                  console.log(`语音消息发送失败：${res.message}`);
                }
              })
              .finally(() => {
                this.scrollToBottom();
              });
          } else {
            this.messages.push({
              text: res.data || '语音消息识别为空',
              time: this.formatTime(new Date()),
              sender: 'user',
            });
            this.scrollToBottom();
          }
          this.$message.success(`语音识别成功 (${recordedDuration}秒)`);
          console.log(`8. ✅ 语音识别结果: "${res.data}"`);
        } else {
          this.$message.error(res.message || '语音识别失败');
          console.warn(`8. ❌ 语音识别失败: ${JSON.stringify(res)}`);
        }
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error('语音上传失败，请检查网络或稍后重试');
      } finally {
        // 无论成功失败，最终都要重置处理状态
        this.isProcessingAudio = false;
        console.groupEnd(); // 关闭调试组
      }
    },

    // 下载音频文件 (用于本地调试)
    downloadAudioFile(audioBlob) {
      try {
        if (!(audioBlob instanceof Blob) || audioBlob.size === 0) {
          console.warn('无法下载: 音频Blob无效');
          return;
        }

        const fileName = `debug_recording_${Date.now()}.wav`;
        const url = URL.createObjectURL(audioBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 延迟释放URL
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        console.log(`   - 已生成调试文件: ${fileName}`);
      } catch (e) {
        console.error('下载调试文件失败:', e);
      }
    },

    // 以下为原有方法，保持不变
    handleTextareaEnter(e) {
      if (e.ctrlKey || e.metaKey) {
        this.inputMessage += '\n';
      } else {
        e.preventDefault();
        this.sendMessage();
      }
    },
    sendMessage() {
      if (this.messages.length) {
        if (!this.inputMessage.trim()) return;
        this.messages.push({
          text: this.inputMessage,
          time: this.formatTime(new Date()),
          sender: 'user',
        });
      }
      sendMessage({
        content: this.inputMessage,
        sessionId: this.sessionId,
        artificial: this.artificial,
      }).then((res) => {
        if (res.code === 200) {
          console.log('文本消息发送成功');
        } else {
          console.log(`文本消息发送失败：${res.message}`);
        }
      });
      this.inputMessage = '';
      this.scrollToBottom();
    },
    colseMessages() {
      closeMessage({ sessionId: this.sessionId }).then((res) => {
        if (res.code === 200) {
          console.log('本次会话结束成功');
        } else {
          console.log(`本次会话结束失败：${res.message}`);
        }
      });
    },
    getWsMessage() {
      DC.registerMessageHandler('MESSAGE', 'kfmsg', this.addmessage);
    },
    addmessage(data) {
      let messageText;
      console.log(data, 'datadatadata');
      try {
        messageText = data.content;
      } catch (e) {
        messageText = data.content;
        console.error('消息解析失败:', e);
      }

      this.messages.push({
        text: messageText,
        time: this.formatTime(new Date()),
        sender: 'service',
      });
      this.sessionId = data.sessionId;
      this.scrollToBottom();
    },
    clearMessages() {
      this.$confirm('确定要清空当前对话记录吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
        customClass: 'confStyle',
      }).then(() => {
        this.messages = [];
        this.sessionId = '';
        this.sendMessage();
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },
    formatTime(date) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    handleBeforeUnload(e) {
      this.colseMessages();
    },
  },
  beforeDestroy() {
    DC.unregisterMessageHandler('MESSAGE', 'kfmsg', this.addmessage);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // 清理录音资源
    if (this.isRecording) this.stopRecording();
    if (this.audioStream) {
      this.audioStream.getTracks().forEach((track) => track.stop());
    }
    this.colseMessages();
  },
};
</script>

<style lang="less" scoped>
.dialog-content {
  display: flex;
  flex-direction: column;
  max-width: inherit;
  height: 400px;
  max-height: 400px;
}

.message-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #f5f7fa;
}

.message-item {
  display: flex;
  margin-bottom: 15px;

  &.user {
    flex-direction: row-reverse;

    .message-content {
      align-items: flex-end;
      margin-right: 10px;
    }

    .message-text {
      background-color: #409eff;
      color: white;
      border-radius: 10px 10px 0 10px;
    }
  }

  &.service {
    .message-content {
      align-items: flex-start;
      margin-left: 10px;
    }

    .message-text {
      background-color: white;
      color: #606266;
      font-size: 16px;
      border-radius: 10px 10px 10px 0;
      border: 1px solid #ebeef5;
    }
    .message-time {
      font-size: 16px;
    }
  }
}
/deep/.el-radio__label {
  font-size: 16px;
}

.message-avatar {
  display: flex;
}

.message-content {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}

.message-text {
  padding: 10px 15px;
  word-break: break-word;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: pre-wrap;
  line-height: 1.5;
}

.message-time {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.input-area {
  .el-textarea__inner {
    resize: none;
  }
}

.action-buttons {
  display: flex;
  justify-content: end;
  margin-top: 10px;

  .el-button + .el-button {
    margin-left: 10px;
  }

  .recording {
    animation: pulse 1.5s infinite;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* 录音状态提示样式 */
.recording-status {
  margin-top: 10px;
  padding: 12px;
  background: linear-gradient(135deg, #fef0f0 0%, #fff 100%);
  border-radius: 4px;
  border: 1px solid #fde2e2;

  .recording-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #f56c6c;
    font-size: 14px;
    font-weight: 500;

    .recording-dot {
      width: 8px;
      height: 8px;
      background-color: #f56c6c;
      border-radius: 50%;
      margin-right: 8px;
      animation: blink 1s infinite;
    }
  }

  .recording-tip {
    text-align: center;
    color: #909399;
    font-size: 12px;
    margin-top: 5px;
  }
}

/* 新增：处理中状态提示样式 */
.processing-status {
  margin-top: 10px;
  padding: 12px;
  background: linear-gradient(135deg, #f0f9ff 0%, #fff 100%);
  border-radius: 4px;
  border: 1px solid #d9ecff;

  .processing-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #409eff;
    font-size: 14px;
    font-weight: 500;

    i {
      margin-right: 8px;
      font-size: 16px;
    }
  }
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.choseServe {
  display: flex;
  justify-content: end;
  margin: 8px 0;
}

@media screen and (max-width: 768px) {
  .message-item {
    &.service {
      .message-text,
      .message-time {
        font-size: 12px !important;
      }
    }
  }

  /deep/.el-radio__label {
    font-size: 12px !important;
  }

  .customer-service-dialog {
    /deep/.el-dialog__body {
      padding: 0 16px;
    }

    .choseServe {
      margin: 10px 0;
    }

    .action-buttons {
      margin-top: 10px;
      .el-button {
        margin-left: 10px;
      }
    }

    .recording-status,
    .processing-status {
      .recording-indicator,
      .processing-indicator {
        font-size: 12px;
      }

      .recording-tip {
        font-size: 11px;
      }
    }
  }
}
</style>
